/**
 * Chat Drafts review surface (M23.2) — /ai/chat-drafts.
 *
 * Everything the AI Chat Panel drafts and the user clicks "Save as
 * draft" on lands here for HR review. Following M19's
 * review-then-approve pattern: a draft is `pending` until HR
 * accepts (keeps it) or discards it. Read + a two-button review —
 * no live mutation happens here (that's M24's chat-configure
 * territory); accepting just marks the draft kept.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, FileText, Sparkles, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import type { ChatDraft } from "../lib/api";

type StatusFilter = "pending" | "accepted" | "discarded";

const _FILTERS: { value: StatusFilter; labelKey: string }[] = [
  { value: "pending", labelKey: "policies.chat_drafts_filter_pending" },
  { value: "accepted", labelKey: "policies.chat_drafts_filter_accepted" },
  { value: "discarded", labelKey: "policies.chat_drafts_filter_discarded" },
];

const _KIND_LABEL_KEYS: Record<string, string> = {
  policy: "policies.chat_drafts_kind_policy",
  job_description: "policies.chat_drafts_kind_job_description",
  termination_reason: "policies.chat_drafts_kind_termination_reason",
  translation: "policies.chat_drafts_kind_translation",
  summary: "policies.chat_drafts_kind_summary",
};

function DraftRow({
  draft,
  onChanged,
}: {
  draft: ChatDraft;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const accept = useMutation({
    mutationFn: () => api.acceptChatDraft(draft.id),
    onSuccess: onChanged,
  });
  const discard = useMutation({
    mutationFn: () => api.discardChatDraft(draft.id),
    onSuccess: onChanged,
  });

  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex items-center gap-2">
        <FileText className="h-4 w-4 shrink-0 text-text-tertiary" />
        <span className="text-sm font-medium text-text-primary">
          {draft.title}
        </span>
        <span className="rounded bg-surface-muted px-1.5 py-0.5 text-[10px] uppercase text-text-secondary">
          {_KIND_LABEL_KEYS[draft.kind] ? t(_KIND_LABEL_KEYS[draft.kind]) : draft.kind}
        </span>
        <span className="ms-auto font-mono text-[10px] text-text-tertiary">
          {draft.created_at.slice(0, 16).replace("T", " ")}
        </span>
      </div>
      <p className="whitespace-pre-wrap rounded-lg border border-border-subtle bg-surface-muted px-2.5 py-2 text-xs text-text-secondary">
        {draft.content.length > 600
          ? `${draft.content.slice(0, 600)}…`
          : draft.content}
      </p>
      {draft.status === "pending" ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => accept.mutate()}
            disabled={accept.isPending || discard.isPending}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-700 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Check size={12} /> {t("policies.chat_drafts_accept")}
          </button>
          <button
            type="button"
            onClick={() => discard.mutate()}
            disabled={accept.isPending || discard.isPending}
            className="inline-flex items-center gap-1 rounded-lg border border-border-subtle px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-muted disabled:opacity-50"
          >
            <Trash2 size={12} /> {t("policies.chat_drafts_discard")}
          </button>
        </div>
      ) : (
        <span
          className={`text-xs ${
            draft.status === "accepted"
              ? "text-emerald-700"
              : "text-text-tertiary"
          }`}
        >
          {draft.status === "accepted"
            ? t("policies.chat_drafts_status_accepted")
            : t("policies.chat_drafts_status_discarded")}
          {draft.reviewed_by_clerk_user_id
            ? ` by ${draft.reviewed_by_clerk_user_id}`
            : ""}
        </span>
      )}
    </li>
  );
}

export function ChatDraftsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>("pending");

  const q = useQuery({
    queryKey: ["chat-drafts", status],
    queryFn: () => api.listChatDrafts(status),
  });

  const refresh = () =>
    qc.invalidateQueries({ queryKey: ["chat-drafts"] });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("policies.chat_drafts_title")}
        subtitle={t("policies.chat_drafts_subtitle")}
        breadcrumbs={[{ label: t("nav.ai"), href: "/ai" }]}
      />

      <div className="flex flex-wrap gap-2">
        {_FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            onClick={() => setStatus(f.value)}
            className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
              status === f.value
                ? "border-brand bg-brand/10 text-brand"
                : "border-border-subtle bg-surface-default text-text-secondary hover:bg-surface-muted"
            }`}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorPanel message={(q.error as Error).message} />
      ) : (q.data ?? []).length === 0 ? (
        <EmptyState
          icon={Sparkles}
          headline={t("policies.chat_drafts_empty_headline", { status: t(`policies.chat_drafts_filter_${status}`).toLowerCase() })}
          description={t("policies.chat_drafts_empty_body")}
        />
      ) : (
        <ul className="divide-y divide-border-subtle rounded-xl border border-border-subtle bg-surface-default">
          {q.data!.map((draft) => (
            <DraftRow
              key={draft.id}
              draft={draft}
              onChanged={refresh}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
